/**
 * UranIA PA — In-memory Storage adapter for oidc-client-ts (US-009).
 *
 * Implementa l'interfaccia DOM `Storage` su Map JS in-process.
 * Usato dal `WebStorageStateStore` di oidc-client-ts per:
 *   - stato PKCE (code_verifier, state, nonce) durante il login round-trip
 *   - oggetto `User` (id_token + access_token + refresh_token) post-login
 *
 * Motivazione (cf. analisi §16.4 + US-009 AC#5):
 *   - access token rimane in memoria del tab e viene rispecchiato nello
 *     Zustand store (vedi `auth-store.ts`);
 *   - NON usiamo `localStorage`/`sessionStorage` → niente esposizione XSS
 *     persistente del refresh_token;
 *   - il refresh viene operato silenziosamente da Keycloak via SSO session
 *     cookie (HttpOnly, SameSite=Lax di default, marcato Secure in staging)
 *     usando l'iframe `automaticSilentRenew` di oidc-client-ts.
 *   - Pattern BFF (cookie SameSite=Strict lato edge) è in scope per Fase 5
 *     post-Traefik 3.x; in Fase 1 la postura sicura equivalente è "niente
 *     token in JS-accessible storage".
 *
 * Il Map è scope-modulo: ricaricare la pagina svuota lo state (atteso per un
 * SPA stateless lato client; Keycloak SSO mantiene la sessione).
 */

class InMemoryStorage implements Storage {
  private readonly store = new Map<string, string>();

  get length(): number {
    return this.store.size;
  }

  clear(): void {
    this.store.clear();
  }

  getItem(key: string): string | null {
    return this.store.get(key) ?? null;
  }

  key(index: number): string | null {
    const keys = Array.from(this.store.keys());
    return keys[index] ?? null;
  }

  removeItem(key: string): void {
    this.store.delete(key);
  }

  setItem(key: string, value: string): void {
    this.store.set(key, value);
  }
}

export const inMemoryStorage: Storage = new InMemoryStorage();
